import { combineReducers } from 'redux';
import constantsum from '../constantsum/reducer';
import fillintheblank from '../fillintheblank/reducer';
import heatmap from '../heatmap/reducer';
import open from '../open/reducer';
import multipart from '../multipart/reducer';
import password from '../password/reducer';
import rating from '../rating/reducer';
import selector from '../selector/reducer';
import timed from '../timed/reducer';
import find from 'lodash/find';
import flow from 'lodash/flow';
import merge from 'lodash/merge';
import bson from 'bson-objectid';
import { prefillOther, prefillWhy } from '../prefillAnswers';

const notification = () => state => state || null;

const questionReducers = {
  constantsum,
  discussion: open,
  heatmap,
  fillintheblank,
  multipart,
  multipleselect: selector,
  notification,
  open,
  password,
  rating,
  singleselect: selector,
  stickynote: heatmap,
  timedheatmap: heatmap,
  timed: timed,
  mediaupload: open
};

const noOpReducer = state => (state === undefined ? null : state);

const createAttachmentReducer = ({ attachments = [] }) => state => {
  if (state === undefined) {
    return attachments.reduce((acc, attachment) => {
      acc[attachment.attachable_id] = attachment;
      return acc;
    }, {});
  }
  return state;
};

const createStaticReducer = (props, key) => state => {
  if (state === undefined) {
    return props[key] || null;
  }
  return state;
};

export const filterQuestionReducer = (props, reducer) => (state, action) => {
  if (state === undefined) {
    return reducer(state, action);
  } else if (action.questionId === props.id) {
    return reducer(state, action);
  } else if (action.type === 'SUBMIT') {
    return reducer(state, action);
  } else {
    return state;
  }
};

export const getServerValidationErrors = (question, { error = [] }) => {
  return find(error, ['id', question.id]);
};

export const createQuestionReducer = props => {
  const type = props._type.split('::')[1].toLowerCase();
  return questionReducers[type]
    ? filterQuestionReducer(props, questionReducers[type](props))
    : noOpReducer;
};

const loadServerValidationErrors = (flash = {}) => question => ({
  ...question,
  serverValidationErrors: getServerValidationErrors(question, flash),
});

const prefillAnswers = (tmap = {}) => question => ({
  ...question,
  tmap: tmap[question.name],
  otherValue: prefillOther(question.name, tmap),
  'why.value': prefillWhy(question.name, tmap),
});

const mergeTmaps = (...sources) => merge(...sources);

const createReducer = (props, persistedState = {}) => {
  const tmap = mergeTmaps(persistedState, props.tmap || {});
  return combineReducers(
    props.questions.reduce(
      (acc, question) => {
        const questionProps = flow(
          loadServerValidationErrors(props.flash),
          prefillAnswers(tmap)
        )(question);
        acc[questionKey(questionProps)] = createQuestionReducer(questionProps);
        return acc;
      },
      {
        attachments: createAttachmentReducer(props),
        attachmentsTmap: createStaticReducer(props, 'attachments_tmap'),
        attachmentConfig: createStaticReducer(props, 'attachmentConfig'),
        token: createStaticReducer(props, 'token'),
      }
    )
  );
};
export default createReducer;

export const questionKey = ({ id, questionId }) =>
  `question_${questionId || id}`;

export const updateWhy = action => state => ({
  ...state,
  why: {
    ...state.why,
    value: action.value,
  },
});

export const updateMatrixWhy = action => state => ({
  ...state,
  rows: state.rows.map(
    (row, idx) =>
      idx === action.rowIndex
        ? {
            ...row,
            why: { ...state.why, value: action.value },
          }
        : row
  ),
});

export const commonQuestionState = props => state => ({
  ...state,
  errorMessages: [],
  isValid: true,
  id: props.id,
  name: props.name,
  otherValue: props.otherValue || '',
  serverValidationErrors: props.serverValidationErrors,
  tempMultiAttachmentKey: props.tempMultiAttachmentKey || bson().str,
  temp_response_attachment_keys: props.temp_response_attachment_keys,
  type: props._type,
  fieldType: props.field_type,
  why: {
    isTouched: false,
    value: props['why.value'] || '',
  },
});
